import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { authClient } from '@lib/betterAuth';
import { PageHeader } from '@resource-kit/ui/react';

type Status = 'idle' | 'verifying' | 'success' | 'error' | 'invalid';

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      setStatus('invalid');
      setMessage(t('auth.verify.invalidLink'));
      return;
    }

    const run = async () => {
      setStatus('verifying');
      try {
        await authClient.verifyEmail({
          query: { token },
        });
        setStatus('success');
        setMessage(t('auth.verify.success'));
      } catch (err: unknown) {
        console.error('Email verification failed:', err);
        setStatus('error');
        setMessage((err instanceof Error ? err.message : undefined) || t('auth.verify.failed'));
      }
    };

    run();
  }, [location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-6 text-center">
        <PageHeader align="center" className="mb-2" title={t('auth.verify.title')} />
        {status === 'verifying' && (
          <p className="text-muted-foreground">{t('auth.verify.verifying')}</p>
        )}
        {status === 'success' && (
          <>
            <p className="text-success mb-4">{message}</p>
            <button
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
              onClick={() => navigate('/login')}
            >
              {t('auth.verify.goToLogin')}
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="text-destructive mb-4">{message}</p>
            <button
              className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2 text-secondary-foreground hover:bg-secondary/80"
              onClick={() => navigate('/login')}
            >
              {t('auth.verify.backToLogin')}
            </button>
          </>
        )}
        {status === 'invalid' && (
          <>
            <p className="text-destructive mb-4">{message}</p>
            <button
              className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2 text-secondary-foreground hover:bg-secondary/80"
              onClick={() => navigate('/login')}
            >
              {t('auth.verify.backToLogin')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
